import { useState, useMemo } from 'react';
import Badge from './Badge';
import EntityLink from './EntityLink';

// Column definitions: key, display label, and a formatter. Not every column
// applies to every ledger type — callers pass which keys are AVAILABLE for
// their specific ledger (e.g. a Vendor ledger never has vendor_name as a
// column since the vendor IS the page you're already on).
const COLUMN_DEFS = {
  side: { label: 'Side', render: v => v ? <Badge variant={v === 'AP' ? 'warning' : 'info'}>{v}</Badge> : '—' },
  contractor_name: { label: 'Contractor' },
  vendor_name: { label: 'Vendor', render: (v, row) => v ? <EntityLink type="vendor" id={row.vendor_id}>{v}</EntityLink> : '—' },
  client_name: { label: 'Client', render: (v, row) => v ? <EntityLink type="client" id={row.client_id}>{v}</EntityLink> : '—' },
  client_wo_number: { label: 'WO #', render: (v, row) => v ? <EntityLink type="work_order" id={row.wo_id}>{v}</EntityLink> : '—' },
  po_number: { label: 'PO #', render: (v, row) => v ? <EntityLink type="purchase_order" id={row.po_id}>{v}</EntityLink> : '—' },
  invoice_number: {
    label: 'Invoice #',
    render: (v, row, mode) => {
      if (!v) return '—';
      const type = mode === 'combined' ? (row.side === 'AP' ? 'ap_invoice' : 'ar_invoice') : (mode === 'ap' ? 'ap_invoice' : 'ar_invoice');
      const link = <EntityLink type={type} id={row.invoice_id}>{v}</EntityLink>;
      return row.is_void ? <>{link} <Badge>void</Badge></> : link;
    },
  },
  invoice_date: { label: 'Invoice Date' },
  period_start: { label: 'Period Start' },
  period_end: { label: 'Period End' },
  hours: { label: 'Hours', render: v => v ?? '—' },
  rate: { label: 'Rate', render: v => v != null ? `$${v}/hr` : '—' },
  amount: { label: 'Amount', render: v => fmt(v) },
  payment_date: { label: 'Payment Date', render: v => v || '—' },
  payment_amount: { label: 'Payment Amount', render: v => v != null ? fmt(v) : '—' },
  payment_ref: { label: 'Payment Ref', render: v => v || '—' },
  verification_status: {
    label: 'Verification',
    render: v => v ? <Badge variant={v === 'verified' ? 'success' : v === 'disputed' ? 'danger' : 'warning'}>{v}</Badge> : '—',
  },
  epd_rate: { label: 'EPD %', render: v => v != null ? `${v}%` : '—' },
  epd_amount: { label: 'EPD Amount', render: v => v != null ? fmt(v) : '—' },
  bgc_amount: { label: 'BGC Amount', render: v => v != null ? fmt(v) : '—' },
  ledger_balance: { label: 'Ledger Balance', render: v => fmt(v) },
  verified_balance: { label: 'Verified Balance', render: v => fmt(v) },
};

function fmt(n) {
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

// Presets define both the available column set AND the default visible
// subset for a given ledger type. Combined gets the side column; AP/AR
// alone don't need it since every row is already known to be that side.
const PRESETS = {
  ap: {
    label: 'AP View',
    columns: ['contractor_name', 'vendor_name', 'client_wo_number', 'po_number', 'invoice_number', 'invoice_date',
      'period_start', 'period_end', 'hours', 'rate', 'amount', 'payment_date', 'payment_amount',
      'payment_ref', 'verification_status', 'ledger_balance', 'verified_balance'],
  },
  ar: {
    label: 'AR View',
    columns: ['contractor_name', 'client_name', 'client_wo_number', 'po_number', 'invoice_number', 'invoice_date',
      'period_start', 'period_end', 'hours', 'rate', 'amount', 'payment_date', 'payment_amount',
      'payment_ref', 'verification_status', 'epd_rate', 'epd_amount', 'bgc_amount', 'ledger_balance', 'verified_balance'],
  },
  combined: {
    label: 'Combined View',
    columns: ['side', 'contractor_name', 'vendor_name', 'client_name', 'client_wo_number', 'po_number', 'invoice_number',
      'invoice_date', 'period_start', 'period_end', 'hours', 'rate', 'amount', 'payment_date', 'payment_amount',
      'payment_ref', 'verification_status', 'epd_rate', 'epd_amount', 'bgc_amount', 'ledger_balance', 'verified_balance'],
  },
};

/**
 * mode: 'ap' | 'ar' | 'combined' — determines the default column preset.
 * rows, summary / apSummary+arSummary: the ledger data from the API.
 * availableColumns: optional override of which columns CAN be toggled on
 *   (defaults to the full set the preset offers) — pass a subset if this
 *   specific entity's ledger genuinely never has certain columns (e.g. a
 *   Vendor's own ledger never needs a vendor_name column toggle at all,
 *   since every row is already that vendor).
 */
export default function LedgerTable({ mode, rows, summary, apSummary, arSummary, availableColumns }) {
  const preset = PRESETS[mode];
  const allColumns = availableColumns || preset.columns;
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(preset.columns.filter(c => allColumns.includes(c))));
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const orderedVisible = useMemo(
    () => allColumns.filter(c => visibleColumns.has(c)),
    [allColumns, visibleColumns]
  );

  function toggleColumn(col) {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  }

  function fmtVal(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {summary && (
            <>
              <SummaryStat label="Total Invoiced" value={fmtVal(summary.total_invoiced)} />
              <SummaryStat label="Ledger Balance" value={fmtVal(summary.ledger_balance)} />
              <SummaryStat label="Verified Balance" value={fmtVal(summary.verified_balance)} highlight />
            </>
          )}
          {apSummary && arSummary && (
            <>
              <SummaryStat label="AP Ledger Bal." value={fmtVal(apSummary.ledger_balance)} />
              <SummaryStat label="AP Verified Bal." value={fmtVal(apSummary.verified_balance)} highlight />
              <SummaryStat label="AR Ledger Bal." value={fmtVal(arSummary.ledger_balance)} />
              <SummaryStat label="AR Verified Bal." value={fmtVal(arSummary.verified_balance)} highlight />
            </>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <button className="btn btn-secondary" onClick={() => setShowColumnPicker(s => !s)}>Columns ▾</button>
          {showColumnPicker && (
            <div className="actions-dropdown-menu" style={{ minWidth: 220, maxHeight: 320, overflowY: 'auto', padding: 8 }}>
              {allColumns.map(col => (
                <label key={col} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={visibleColumns.has(col)} onChange={() => toggleColumn(col)} />
                  {COLUMN_DEFS[col]?.label || col}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="state-block">
          <div className="state-block-title">No transactions yet</div>
          <div>Invoices and payments for this entity will appear here once they exist.</div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              {orderedVisible.map(col => <th key={col}>{COLUMN_DEFS[col]?.label || col}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              // Void invoices stay listed but are outside every total and balance.
              <tr key={i} style={row.is_void ? { opacity: 0.5 } : undefined} title={row.is_void ? 'Void - not counted in totals or balances' : undefined}>
                {orderedVisible.map(col => (
                  <td key={col}>
                    {COLUMN_DEFS[col]?.render ? COLUMN_DEFS[col].render(row[col], row, mode) : (row[col] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SummaryStat({ label, value, highlight }) {
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: highlight ? 'var(--primary)' : undefined }}>{value}</div>
    </div>
  );
}
